import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { useLang } from "../lib/i18n";
import { scrollToId } from "../lib/lenis";

const ease = [0.22, 1, 0.36, 1];

const MaskLine = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${className}`} initial={{ y: "105%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay: 0.15 + i * 0.12, ease }}>
      {children}
    </motion.span>
  </span>
);

const BrandPhoto = () => {
  return (
    <div className="relative">
      <motion.div
        initial={{ opacity: 0, clipPath: "inset(100% 0 0 0)" }} animate={{ opacity: 1, clipPath: "inset(0% 0 0 0)" }}
        transition={{ duration: 1.4, delay: 0.5, ease }} className="relative aspect-square w-full overflow-hidden rounded-[22px] border border-emerald-500/30 bg-[#0C1210] shadow-[0_40px_120px_-30px_rgba(0,230,118,0.35)]" data-testid="hero-tilt-card">
        <picture>
          <source type="image/webp" srcSet="/images/korani-facade-480.webp 480w, /images/korani-facade-920.webp 920w, /images/korani-facade-1254.webp 1254w" sizes="(min-width: 1024px) 560px, 92vw" />
          <img src="/images/korani-facade-920.jpg" alt="KORANI CONSTRUCTION GROUP – Κατασκευές και Ανακαινίσεις" width="1254" height="1254"
            fetchpriority="high" decoding="async" className="h-full w-full object-cover" data-testid="hero-brand-image" />
        </picture>
        <div className="absolute left-4 top-4 rounded-full border border-emerald-400/40 bg-[#050706]/70 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-white backdrop-blur-md">
          <span className="mr-2 inline-block h-2 w-2 animate-pulse rounded-full bg-[#00E676]" />ΚΙΛΚΙΣ
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const lines = [...t.hero.lines];

  return (
    <section ref={ref} id="top" className="relative min-h-[100svh] overflow-hidden pt-[110px] pb-16 sm:pt-[130px]" data-testid="hero-section">
      <div className="hero-glow" />
      <div className="grid-lines absolute inset-0 opacity-[0.07]" />
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-12 px-5 sm:px-8 lg:grid-cols-12">
        <motion.div style={{ y: yText }} className="lg:col-span-7" key={lang}>
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease }} className="eyebrow mb-7" data-testid="hero-eyebrow">
            <span className="mr-3 inline-block h-px w-10 align-middle bg-[#00E676]" />{t.hero.eyebrow}
          </motion.p>
          <h1 className="sr-only" data-testid="seo-h1">KORANI CONSTRUCTION GROUP — {t.hero.seo}</h1>
          <p aria-hidden="false" className="font-display text-[2.6rem] font-black uppercase leading-[0.95] tracking-[-0.02em] text-white sm:text-6xl lg:text-[5.4rem]" data-testid="hero-title">
            {lines.map((l, i) => <MaskLine key={l} i={i} className={i === 1 ? "text-outline" : ""}>{l}</MaskLine>)}
            <MaskLine i={3} className="text-[#00E676]">{t.hero.accent}</MaskLine>
          </p>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.8, ease }} className="mt-8 max-w-xl text-base leading-relaxed text-zinc-300 md:text-lg" data-testid="hero-subtitle">
            {t.hero.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.95, ease }} className="mt-10 flex flex-wrap gap-4">
            <button onClick={() => scrollToId("quote")} className="btn-primary btn-lg" data-testid="hero-quote-btn">{t.hero.cta} <ArrowUpRight className="h-5 w-5" /></button>
            <button onClick={() => scrollToId("services")} className="btn-ghost btn-lg" data-testid="hero-services-btn">{t.hero.cta2} <ArrowDownRight className="h-5 w-5" /></button>
          </motion.div>
          <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.15 }} className="mt-12 flex flex-wrap gap-x-8 gap-y-3 font-mono text-xs uppercase tracking-[0.2em] text-zinc-300">
            {t.hero.badges.map((b) => <li key={b} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rotate-45 bg-[#00E676]" />{b}</li>)}
          </motion.ul>
        </motion.div>
        <motion.div style={{ y: yCard }} className="mx-auto w-full max-w-[560px] lg:col-span-5">
          <BrandPhoto />
        </motion.div>
      </div>
      <div className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400 lg:flex">
        {t.hero.scroll}<span className="scroll-line" />
      </div>
    </section>
  );
};
